import React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Download,
  Printer,
  TrendingUp,
  ShoppingCart,
  DollarSign,
  Package,
} from "lucide-react";
import { format } from "date-fns";
import { useStock } from "@/context/StockContext";
import { Sale } from "@/types/stock";
import { useRepairShopContext } from "@/context/RepairShopContext";

interface DailySummaryReportProps {
  touchMode: boolean;
}

const DailySummaryReport: React.FC<DailySummaryReportProps> = ({
  touchMode,
}) => {
  const { t } = useTranslation();
  const { sales, products } = useStock();
  const { repairShop } = useRepairShopContext();

  // Filter today's sales
  const todaysSales = sales.filter(
    (sale) =>
      format(new Date(sale.createdAt), "yyyy-MM-dd") ===
      format(new Date(), "yyyy-MM-dd")
  );

  // Calculate metrics
  const todaysRevenue = todaysSales.reduce(
    (sum, sale) => sum + sale.totalAmount,
    0
  );

  const calculateSaleProfit = (sale: Sale): number => {
    if (!sale.items || sale.items.length === 0) return 0;
    const totalCost = sale.items.reduce((sum, item) => {
      const productCost = item.product?.cost || 0;
      return sum + productCost * item.quantity;
    }, 0);
    return sale.totalAmount - totalCost;
  };

  const todaysProfit = todaysSales.reduce(
    (sum, sale) => sum + calculateSaleProfit(sale),
    0
  );

  const totalItemsSold = todaysSales.reduce(
    (sum, sale) =>
      sum +
      (sale.items?.reduce((itemSum, item) => itemSum + item.quantity, 0) || 0),
    0
  );

  // Payment method breakdown
  const paymentBreakdown = todaysSales.reduce((acc, sale) => {
    acc[sale.paymentMethod] = (acc[sale.paymentMethod] || 0) + sale.totalAmount;
    return acc;
  }, {} as Record<string, number>);

  // Low stock products
  const lowStockProducts = products.filter(
    (p) =>
      p.isActive && p.stockQuantity > 0 && p.stockQuantity <= p.minStockLevel
  );

  const outOfStockProducts = products.filter(
    (p) => p.isActive && p.stockQuantity === 0
  );

  const formatCurrency = (amount: number) => `${amount.toFixed(3)} TND`;

  const handlePrint = () => {
    // Generate detailed HTML report
    const reportHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Daily Summary Report - ${format(
          new Date(),
          "yyyy-MM-dd"
        )}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          .header h1 {
            margin: 0;
            color: #2563eb;
          }
          .header p {
            margin: 5px 0;
            color: #666;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 20px;
            margin-bottom: 30px;
          }
          .summary-card {
            border: 1px solid #ddd;
            padding: 15px;
            border-radius: 8px;
            text-align: center;
          }
          .summary-card h3 {
            margin: 0 0 10px 0;
            font-size: 14px;
            color: #666;
            text-transform: uppercase;
          }
          .summary-card .value {
            font-size: 24px;
            font-weight: bold;
            color: #2563eb;
          }
          .summary-card.profit .value {
            color: #059669;
          }
          .section {
            margin-bottom: 30px;
          }
          .section h2 {
            border-bottom: 2px solid #2563eb;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          th, td {
            padding: 10px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }
          th {
            background-color: #f3f4f6;
            font-weight: bold;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .total-row {
            font-weight: bold;
            background-color: #f9fafb;
          }
          .payment-breakdown {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
          }
          .payment-item {
            border: 1px solid #ddd;
            padding: 15px;
            border-radius: 8px;
          }
          .alert-box {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
          .alert-item {
            padding: 15px;
            border-radius: 8px;
            text-align: center;
          }
          .alert-item.warning {
            background-color: #fef3c7;
            border: 1px solid #fbbf24;
          }
          .alert-item.danger {
            background-color: #fee2e2;
            border: 1px solid #ef4444;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${repairShop?.name || "Repair Shop"}</h1>
          <p>${repairShop?.address || ""}</p>
          <p>${repairShop?.phone || ""}</p>
          <h2>Daily Summary Report</h2>
          <p>${format(new Date(), "EEEE, MMMM dd, yyyy")}</p>
        </div>

        <div class="summary-grid">
          <div class="summary-card">
            <h3>Total Sales</h3>
            <div class="value">${todaysSales.length}</div>
          </div>
          <div class="summary-card">
            <h3>Revenue</h3>
            <div class="value">${formatCurrency(todaysRevenue)}</div>
          </div>
          <div class="summary-card profit">
            <h3>Profit</h3>
            <div class="value">${formatCurrency(todaysProfit)}</div>
          </div>
          <div class="summary-card">
            <h3>Items Sold</h3>
            <div class="value">${totalItemsSold}</div>
          </div>
        </div>

        <div class="section">
          <h2>Sales Details</h2>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Time</th>
                <th>Customer</th>
                <th>Items</th>
                <th class="text-right">Subtotal</th>
                <th class="text-right">Discount</th>
                <th class="text-right">Total</th>
                <th class="text-center">Payment</th>
                <th class="text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              ${todaysSales
                .map(
                  (sale, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${format(new Date(sale.createdAt), "HH:mm:ss")}</td>
                  <td>${sale.customerName || "-"}</td>
                  <td>${sale.items?.length || 0}</td>
                  <td class="text-right">${formatCurrency(sale.subtotal)}</td>
                  <td class="text-right">${formatCurrency(
                    sale.discountAmount
                  )}</td>
                  <td class="text-right">${formatCurrency(
                    sale.totalAmount
                  )}</td>
                  <td class="text-center">${sale.paymentMethod}</td>
                  <td class="text-right">${formatCurrency(
                    calculateSaleProfit(sale)
                  )}</td>
                </tr>
                ${
                  sale.items
                    ?.map(
                      (item) => `
                  <tr style="background-color: #f9fafb; font-size: 12px;">
                    <td colspan="3" style="padding-left: 30px;">→ ${
                      item.product?.name || "Unknown"
                    }</td>
                    <td>Qty: ${item.quantity}</td>
                    <td class="text-right">${formatCurrency(
                      item.unitPrice
                    )}</td>
                    <td class="text-right">${formatCurrency(
                      item.discount || 0
                    )}</td>
                    <td class="text-right">${formatCurrency(
                      item.totalPrice
                    )}</td>
                    <td colspan="2"></td>
                  </tr>
                `
                    )
                    .join("") || ""
                }
              `
                )
                .join("")}
              <tr class="total-row">
                <td colspan="6">TOTAL</td>
                <td class="text-right">${formatCurrency(todaysRevenue)}</td>
                <td></td>
                <td class="text-right">${formatCurrency(todaysProfit)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="section">
          <h2>Payment Breakdown</h2>
          <div class="payment-breakdown">
            ${Object.entries(paymentBreakdown)
              .map(
                ([method, amount]) => `
              <div class="payment-item">
                <h3 style="margin: 0 0 10px 0; text-transform: capitalize;">${method}</h3>
                <div style="font-size: 20px; font-weight: bold; color: #2563eb;">${formatCurrency(
                  amount
                )}</div>
              </div>
            `
              )
              .join("")}
          </div>
        </div>

        <div class="section">
          <h2>Inventory Alerts</h2>
          <div class="alert-box">
            <div class="alert-item warning">
              <h3 style="margin: 0 0 10px 0;">Low Stock</h3>
              <div style="font-size: 24px; font-weight: bold; color: #d97706;">${
                lowStockProducts.length
              }</div>
              ${
                lowStockProducts.length > 0
                  ? `
                <ul style="text-align: left; margin-top: 10px; font-size: 12px;">
                  ${lowStockProducts
                    .slice(0, 5)
                    .map(
                      (p) => `
                    <li>${p.name} (${p.stockQuantity} left)</li>
                  `
                    )
                    .join("")}
                  ${
                    lowStockProducts.length > 5
                      ? `<li>... and ${lowStockProducts.length - 5} more</li>`
                      : ""
                  }
                </ul>
              `
                  : ""
              }
            </div>
            <div class="alert-item danger">
              <h3 style="margin: 0 0 10px 0;">Out of Stock</h3>
              <div style="font-size: 24px; font-weight: bold; color: #dc2626;">${
                outOfStockProducts.length
              }</div>
              ${
                outOfStockProducts.length > 0
                  ? `
                <ul style="text-align: left; margin-top: 10px; font-size: 12px;">
                  ${outOfStockProducts
                    .slice(0, 5)
                    .map(
                      (p) => `
                    <li>${p.name}</li>
                  `
                    )
                    .join("")}
                  ${
                    outOfStockProducts.length > 5
                      ? `<li>... and ${outOfStockProducts.length - 5} more</li>`
                      : ""
                  }
                </ul>
              `
                  : ""
              }
            </div>
          </div>
        </div>

        <div style="margin-top: 50px; text-align: center; color: #666; font-size: 12px;">
          <p>Generated on ${format(new Date(), "yyyy-MM-dd HH:mm:ss")}</p>
        </div>
      </body>
      </html>
    `;

    // Open in new window and print
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(reportHTML);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const handleExport = () => {
    // Generate the same HTML report for export
    const reportHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Daily Summary Report - ${format(
          new Date(),
          "yyyy-MM-dd"
        )}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          .header h1 {
            margin: 0;
            color: #2563eb;
          }
          .header p {
            margin: 5px 0;
            color: #666;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 20px;
            margin-bottom: 30px;
          }
          .summary-card {
            border: 1px solid #ddd;
            padding: 15px;
            border-radius: 8px;
            text-align: center;
          }
          .summary-card h3 {
            margin: 0 0 10px 0;
            font-size: 14px;
            color: #666;
            text-transform: uppercase;
          }
          .summary-card .value {
            font-size: 24px;
            font-weight: bold;
            color: #2563eb;
          }
          .summary-card.profit .value {
            color: #059669;
          }
          .section {
            margin-bottom: 30px;
          }
          .section h2 {
            border-bottom: 2px solid #2563eb;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          th, td {
            padding: 10px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }
          th {
            background-color: #f3f4f6;
            font-weight: bold;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .total-row {
            font-weight: bold;
            background-color: #f9fafb;
          }
          .payment-breakdown {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
          }
          .payment-item {
            border: 1px solid #ddd;
            padding: 15px;
            border-radius: 8px;
          }
          .alert-box {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
          .alert-item {
            padding: 15px;
            border-radius: 8px;
            text-align: center;
          }
          .alert-item.warning {
            background-color: #fef3c7;
            border: 1px solid #fbbf24;
          }
          .alert-item.danger {
            background-color: #fee2e2;
            border: 1px solid #ef4444;
          }
          @media print {
            body { margin: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${repairShop?.name || "Repair Shop"}</h1>
          <p>${repairShop?.address || ""}</p>
          <p>${repairShop?.phone || ""}</p>
          <h2>Daily Summary Report</h2>
          <p>${format(new Date(), "EEEE, MMMM dd, yyyy")}</p>
        </div>

        <div class="summary-grid">
          <div class="summary-card">
            <h3>Total Sales</h3>
            <div class="value">${todaysSales.length}</div>
          </div>
          <div class="summary-card">
            <h3>Revenue</h3>
            <div class="value">${formatCurrency(todaysRevenue)}</div>
          </div>
          <div class="summary-card profit">
            <h3>Profit</h3>
            <div class="value">${formatCurrency(todaysProfit)}</div>
          </div>
          <div class="summary-card">
            <h3>Items Sold</h3>
            <div class="value">${totalItemsSold}</div>
          </div>
        </div>

        <div class="section">
          <h2>Sales Details</h2>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Time</th>
                <th>Customer</th>
                <th>Items</th>
                <th class="text-right">Subtotal</th>
                <th class="text-right">Discount</th>
                <th class="text-right">Total</th>
                <th class="text-center">Payment</th>
                <th class="text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              ${todaysSales
                .map(
                  (sale, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${format(new Date(sale.createdAt), "HH:mm:ss")}</td>
                  <td>${sale.customerName || "-"}</td>
                  <td>${sale.items?.length || 0}</td>
                  <td class="text-right">${formatCurrency(sale.subtotal)}</td>
                  <td class="text-right">${formatCurrency(
                    sale.discountAmount
                  )}</td>
                  <td class="text-right">${formatCurrency(
                    sale.totalAmount
                  )}</td>
                  <td class="text-center">${sale.paymentMethod}</td>
                  <td class="text-right">${formatCurrency(
                    calculateSaleProfit(sale)
                  )}</td>
                </tr>
                ${
                  sale.items
                    ?.map(
                      (item) => `
                  <tr style="background-color: #f9fafb; font-size: 12px;">
                    <td colspan="3" style="padding-left: 30px;">→ ${
                      item.product?.name || "Unknown"
                    }</td>
                    <td>Qty: ${item.quantity}</td>
                    <td class="text-right">${formatCurrency(
                      item.unitPrice
                    )}</td>
                    <td class="text-right">${formatCurrency(
                      item.discount || 0
                    )}</td>
                    <td class="text-right">${formatCurrency(
                      item.totalPrice
                    )}</td>
                    <td colspan="2"></td>
                  </tr>
                `
                    )
                    .join("") || ""
                }
              `
                )
                .join("")}
              <tr class="total-row">
                <td colspan="6">TOTAL</td>
                <td class="text-right">${formatCurrency(todaysRevenue)}</td>
                <td></td>
                <td class="text-right">${formatCurrency(todaysProfit)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="section">
          <h2>Payment Breakdown</h2>
          <div class="payment-breakdown">
            ${Object.entries(paymentBreakdown)
              .map(
                ([method, amount]) => `
              <div class="payment-item">
                <h3 style="margin: 0 0 10px 0; text-transform: capitalize;">${method}</h3>
                <div style="font-size: 20px; font-weight: bold; color: #2563eb;">${formatCurrency(
                  amount
                )}</div>
              </div>
            `
              )
              .join("")}
          </div>
        </div>

        <div class="section">
          <h2>Inventory Alerts</h2>
          <div class="alert-box">
            <div class="alert-item warning">
              <h3 style="margin: 0 0 10px 0;">Low Stock</h3>
              <div style="font-size: 24px; font-weight: bold; color: #d97706;">${
                lowStockProducts.length
              }</div>
              ${
                lowStockProducts.length > 0
                  ? `
                <ul style="text-align: left; margin-top: 10px; font-size: 12px;">
                  ${lowStockProducts
                    .slice(0, 5)
                    .map(
                      (p) => `
                    <li>${p.name} (${p.stockQuantity} left)</li>
                  `
                    )
                    .join("")}
                  ${
                    lowStockProducts.length > 5
                      ? `<li>... and ${lowStockProducts.length - 5} more</li>`
                      : ""
                  }
                </ul>
              `
                  : ""
              }
            </div>
            <div class="alert-item danger">
              <h3 style="margin: 0 0 10px 0;">Out of Stock</h3>
              <div style="font-size: 24px; font-weight: bold; color: #dc2626;">${
                outOfStockProducts.length
              }</div>
              ${
                outOfStockProducts.length > 0
                  ? `
                <ul style="text-align: left; margin-top: 10px; font-size: 12px;">
                  ${outOfStockProducts
                    .slice(0, 5)
                    .map(
                      (p) => `
                    <li>${p.name}</li>
                  `
                    )
                    .join("")}
                  ${
                    outOfStockProducts.length > 5
                      ? `<li>... and ${outOfStockProducts.length - 5} more</li>`
                      : ""
                  }
                </ul>
              `
                  : ""
              }
            </div>
          </div>
        </div>

        <div style="margin-top: 50px; text-align: center; color: #666; font-size: 12px;">
          <p>Generated on ${format(new Date(), "yyyy-MM-dd HH:mm:ss")}</p>
        </div>
      </body>
      </html>
    `;

    // Download as HTML file
    const dataBlob = new Blob([reportHTML], { type: "text/html" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `daily-summary-${format(new Date(), "yyyy-MM-dd")}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">
                {t("stock.dailySummary")}
              </CardTitle>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {format(new Date(), "EEEE, MMMM dd, yyyy")}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                {t("common.print")}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                {t("common.download")}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Key Metrics */}
      <div
        className={`grid gap-4 ${touchMode ? "grid-cols-2" : "grid-cols-4"}`}
      >
        <Card>
          <CardContent className="p-6 text-center">
            <ShoppingCart className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-3xl font-bold">{todaysSales.length}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("stock.totalSales")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <DollarSign className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-3xl font-bold text-green-600">
              {formatCurrency(todaysRevenue)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("stock.revenue")}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20">
          <CardContent className="p-6 text-center">
            <TrendingUp className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
            <p className="text-3xl font-bold text-emerald-600">
              {formatCurrency(todaysProfit)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("stock.profit")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <Package className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <p className="text-3xl font-bold">{totalItemsSold}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("stock.itemsSold")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Payment Methods & Inventory Alerts */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Payment Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {t("stock.paymentBreakdown")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(paymentBreakdown).map(([method, amount]) => (
              <div key={method} className="flex justify-between items-center">
                <span className="capitalize">{t(`stock.${method}`)}</span>
                <span className="font-semibold">{formatCurrency(amount)}</span>
              </div>
            ))}
            {Object.keys(paymentBreakdown).length === 0 && (
              <p className="text-center text-gray-500 py-4">
                {t("stock.noSalesToday")}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Inventory Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {t("stock.inventoryAlerts")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <span className="text-sm">{t("stock.lowStock")}</span>
              <span className="font-bold text-yellow-600">
                {lowStockProducts.length}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <span className="text-sm">{t("stock.outOfStock")}</span>
              <span className="font-bold text-red-600">
                {outOfStockProducts.length}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DailySummaryReport;
